import { useState, useEffect } from 'react';
import { HistoryIcon } from 'lucide-react';
import { getCreditTransactions } from '@/lib/api/credits';
import type { CreditTransaction } from '@/types/database.types';
import CreditTransactionItem from './CreditTransactionItem';
import Pagination from './Pagination';

interface CreditTransactionListProps {
  limit?: number;
  showPagination?: boolean;
  className?: string;
}

export default function CreditTransactionList({ 
  limit = 5, 
  showPagination = true,
  className = ''
}: CreditTransactionListProps) {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, limit]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await getCreditTransactions(currentPage, limit);
      setTransactions(response.data);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex justify-center py-8 ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-600"></div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <HistoryIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
        <p className="text-gray-500">
          Participate in CashSnaps to start earning credits!
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {transactions.map(transaction => (
          <CreditTransactionItem key={transaction.id} transaction={transaction} />
        ))}
      </div>
      
      {showPagination && totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );
}